/**
 * TariffSelector Component
 *
 * Allows users to select their electricity tariff.
 * Supports OSD-aware filtering to show only available tariffs.
 *
 * @see /frontend/src/utils/osdMetadata.ts - OSD metadata
 */

import { useMemo } from 'react';
import { Radio, Stack, Text, Group, Paper, Badge } from '@mantine/core';
import { IconSun, IconMoon, IconClock, IconCalendar, IconFlame, IconBolt } from '@tabler/icons-react';
import { TariffType } from '../../../../types/graphql';
import { OSDCode, getAvailableTariffsForOSD, OSD_METADATA } from '../../../../utils/osdMetadata';

export interface TariffSelectorProps {
  /** Currently selected tariff */
  value: string | null;
  /** Callback when tariff selection changes */
  onChange: (value: string) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Optional OSD to filter available tariffs */
  osd?: OSDCode;
  /** Show OSD-specific badges */
  showOSDBadges?: boolean;
}

interface TariffOption {
  value: TariffType;
  label: string;
  description: string;
  icon: typeof IconSun;
  osdSpecific?: OSDCode; // If set, tariff is specific to this OSD
}

/**
 * All possible tariff options
 * Includes OSD-specific tariffs with their requirements
 */
const ALL_TARIFF_OPTIONS: TariffOption[] = [
  {
    value: TariffType.G11,
    label: 'G11 - Jednostrefowa',
    description: 'Stała cena przez całą dobę (24h)',
    icon: IconSun,
  },
  {
    value: TariffType.G12,
    label: 'G12 - Dwustrefowa',
    description: 'Dzień i noc (różne stawki w dzień i w nocy)',
    icon: IconClock,
  },
  {
    value: TariffType.G12w,
    label: 'G12w - Dwustrefowa weekendowa',
    description: 'Niższa cena w weekendy i święta',
    icon: IconCalendar,
  },
  {
    value: TariffType.G12as,
    label: 'G12as - Antysmogowa',
    description: 'Dla ogrzewania elektrycznego (pompa ciepła, piec akumulacyjny)',
    icon: IconFlame,
  },
  {
    value: TariffType.G13,
    label: 'G13 - Trzystrefowa',
    description: 'Szczyt, reszta dnia i noc (3 strefy czasowe)',
    icon: IconMoon,
    osdSpecific: 'TAURON',
  },
  {
    value: TariffType.G13s,
    label: 'G13s - Trzystrefowa sezonowa',
    description: 'Najtaniej w godzinach produkcji PV (09-17 lato)',
    icon: IconMoon,
    osdSpecific: 'TAURON',
  },
  {
    value: TariffType.G14,
    label: 'G14 - Dynamiczna RCE',
    description: 'Ceny zmienne co godzinę wg rynku energii',
    icon: IconBolt,
    osdSpecific: 'TAURON',
  },
];

/**
 * Get filtered tariff options based on OSD availability
 */
function getFilteredTariffOptions(osd?: OSDCode): TariffOption[] {
  if (!osd) {
    // No OSD filter - show all common tariffs (G11, G12, G12w)
    return ALL_TARIFF_OPTIONS.filter(
      (opt) => !opt.osdSpecific || opt.osdSpecific === 'TAURON'
    );
  }

  const availableTariffs = getAvailableTariffsForOSD(osd);

  return ALL_TARIFF_OPTIONS.filter((opt) =>
    availableTariffs.includes(opt.value)
  );
}

export function TariffSelector({
  value,
  onChange,
  disabled,
  osd,
  showOSDBadges = false,
}: TariffSelectorProps) {
  // Filter tariffs based on OSD
  const tariffOptions = useMemo(() => getFilteredTariffOptions(osd), [osd]);

  // Get OSD name for display
  const osdName = osd ? OSD_METADATA[osd].shortName : undefined;

  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <div>
          <Text fw={500} size="sm" mb={4}>
            Wybierz taryfę
          </Text>
          <Text size="xs" c="dimmed">
            {osd
              ? `Taryfy dostępne u operatora ${osdName}`
              : 'Taryfa musi odpowiadać taryfie na Twojej fakturze'}
          </Text>
        </div>

        <Radio.Group value={value || ''} onChange={onChange}>
          <Stack gap="xs">
            {tariffOptions.map((option) => {
              const Icon = option.icon;
              return (
                <Radio
                  key={option.value}
                  value={option.value}
                  disabled={disabled}
                  label={
                    <Group gap="xs" wrap="nowrap">
                      <Icon size={16} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <Group gap="xs" justify="space-between">
                          <Text size="sm" fw={500}>
                            {option.label}
                          </Text>
                          {showOSDBadges && option.osdSpecific && (
                            <Badge
                              size="xs"
                              variant="light"
                              color={
                                option.osdSpecific === 'TAURON'
                                  ? 'grape'
                                  : option.osdSpecific === 'PGE'
                                  ? 'red'
                                  : option.osdSpecific === 'ENEA'
                                  ? 'green'
                                  : 'orange'
                              }
                            >
                              {OSD_METADATA[option.osdSpecific].shortName}
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {option.description}
                        </Text>
                      </div>
                    </Group>
                  }
                  styles={{
                    label: { width: '100%' },
                    body: { alignItems: 'flex-start' },
                  }}
                />
              );
            })}
          </Stack>
        </Radio.Group>

        {tariffOptions.length === 0 && (
          <Text size="sm" c="dimmed" ta="center">
            Brak dostępnych taryf dla wybranego OSD
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
